import type { AppDispatch } from '@/app/store/store';
import { plpApi } from '@/features/plp/infra/adapters/plpListingApi';
import type { PlpListingRequestArgs } from '@/features/plp/infra/adapters/plpListingApi';
import type { SeoRepository, FetchSeoParams, FetchSeoOptions, SeoResult } from '../../domain/interfaces/SeoRepository';
import type { PlpSeoDataResponseDTO } from '../dto/seo.dto';
import { plainToListingSeo, adaptFaqsFromPlain } from '../mappers/seo.mapper';

async function dispatchQuery<T>(
  dispatch: AppDispatch,
  initiateResult: unknown,
  signal?: AbortSignal
): Promise<T> {
  const pendingQuery = dispatch(
    initiateResult as unknown as Parameters<AppDispatch>[0]
  ) as unknown as { unwrap: () => Promise<T>; abort: () => void };

  const onAbort = () => {
    pendingQuery.abort();
  };
  if (signal) {
    if (signal.aborted) {
      onAbort();
    } else {
      signal.addEventListener('abort', onAbort);
    }
  }
  try {
    return await pendingQuery.unwrap();
  } finally {
    if (signal) {
      signal.removeEventListener('abort', onAbort);
    }
  }
}

function isAbortError(error: unknown, signal?: AbortSignal): boolean {
  return (
    Boolean(signal?.aborted) ||
    (error instanceof DOMException && error.name === 'AbortError') ||
    (typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      (error as { name: string }).name === 'AbortError')
  );
}

export class SeoAdapter implements SeoRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async fetchSeoData(params: FetchSeoParams, options?: FetchSeoOptions): Promise<SeoResult> {
    const signal = options?.signal;
    try {
      const dto = await dispatchQuery<PlpSeoDataResponseDTO>(
        this.dispatch,
        plpApi.endpoints.getSeoData.initiate(params as PlpListingRequestArgs),
        signal
      );
      const plain = (dto ?? {}) as Record<string, unknown>;
      return {
        listingSeo: plainToListingSeo(plain),
        faqs: adaptFaqsFromPlain(plain),
      };
    } catch (error) {
      if (isAbortError(error, signal)) {
        throw new DOMException('Aborted', 'AbortError');
      }
      console.error('Error fetching SEO data:', error);
      return {};
    }
  }
}
